import { Link, useLocation } from "react-router-dom";
import { useState } from "react";

import { AbcFitnessBrandLogo } from "@/components/portal/AbcFitnessBrandLogo";
import { HomeTopNav } from "@/components/portal/HomeTopNav";
import { usePageTitle } from "@/hooks/usePageTitle";
import { useVerificationResendCooldown } from "@/hooks/useVerificationResendCooldown";
import { registerUser } from "@/lib/api/register-user";
import { resendVerification } from "@/lib/api/resend-verification";
import {
  getRegisterErrorMessage,
  isSignupFormValid,
  type RegisterErrorResult,
  toRegisterUserRequest,
} from "@/lib/api/signup-form";
import { formatVerificationResendCooldownLabel } from "@/lib/api/verification-resend-cooldown";
import {
  getResendVerificationErrorMessage,
  RESEND_VERIFICATION_SUCCESS_MESSAGE,
} from "@/lib/api/verify-email-form";
import {
  getRequestAccessAuthNotice,
  readAuthReturnPath,
  resolvePostAuthRedirect,
} from "@/lib/auth/post-auth-redirect";

function SignupErrorBanner({
  error,
  loginLinkState,
}: {
  error: RegisterErrorResult;
  loginLinkState?: { from: string };
}) {
  return (
    <div className="error-msg" data-testid="home-signup-error" role="alert">
      <i className="ti ti-alert-circle" />
      <span>
        {error.message}{" "}
        {error.duplicateEmail ? (
          <Link
            to="/login"
            state={loginLinkState}
            data-testid="home-signup-error-login-link"
          >
            Log in →
          </Link>
        ) : null}
      </span>
    </div>
  );
}

export default function SignupPage() {
  usePageTitle("Sign up");
  const location = useLocation();
  const returnPath = resolvePostAuthRedirect(readAuthReturnPath(location.state));
  const requestAccessNotice = getRequestAccessAuthNotice(
    readAuthReturnPath(location.state),
  );
  const loginLinkState =
    returnPath !== "/" ? { from: returnPath } : undefined;
  const [companyName, setCompanyName] = useState("");
  const [contactName, setContactName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<RegisterErrorResult | null>(null);
  const [registeredEmail, setRegisteredEmail] = useState<string | null>(null);
  const [resendLoading, setResendLoading] = useState(false);
  const [resendNotice, setResendNotice] = useState<string | null>(null);
  const [resendError, setResendError] = useState<string | null>(null);
  const resendCooldown = useVerificationResendCooldown();

  const formValues = {
    companyName,
    contactName,
    email,
    password,
  };
  const isValid = isSignupFormValid(formValues);

  function clearError() {
    setError(null);
  }

  function getPasswordStrength(pw: string): {
    width: string;
    color: string;
    label: string;
  } {
    let strength = 0;
    if (pw.length >= 8) strength++;
    if (/[A-Z]/.test(pw)) strength++;
    if (/[0-9]/.test(pw)) strength++;
    if (/[^A-Za-z0-9]/.test(pw)) strength++;
    const colors = ["", "#e24b4a", "#f59f00", "#29A9E1", "#00B588"];
    const labels = ["Enter a password", "Weak", "Fair", "Good", "Strong"];
    const widths = ["0%", "25%", "50%", "75%", "100%"];
    return {
      width: widths[strength],
      color: colors[strength],
      label: pw.length ? labels[strength] : "Enter a password",
    };
  }

  const pwStrength = getPasswordStrength(password);

  async function handleSubmit(event?: { preventDefault(): void }) {
    event?.preventDefault();
    if (!isValid) return;
    setLoading(true);
    setError(null);
    setResendNotice(null);
    setResendError(null);

    try {
      await registerUser(toRegisterUserRequest(formValues));
      setRegisteredEmail(email.trim());
      resendCooldown.start();
    } catch (err: unknown) {
      setError(getRegisterErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }

  async function handleResend(event?: { preventDefault(): void }) {
    event?.preventDefault();
    if (!registeredEmail || resendCooldown.isCoolingDown) {
      return;
    }

    setResendLoading(true);
    setResendError(null);
    setResendNotice(null);

    try {
      const response = await resendVerification({ email: registeredEmail });
      setResendNotice(response.message || RESEND_VERIFICATION_SUCCESS_MESSAGE);
      resendCooldown.start();
    } catch (err: unknown) {
      setResendError(getResendVerificationErrorMessage(err));
    } finally {
      setResendLoading(false);
    }
  }

  if (registeredEmail) {
    return (
      <>
        <HomeTopNav />
        <main id="main-content" className="auth-shell">
          <div style={{ width: "100%", maxWidth: 560 }}>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                marginBottom: "1.5rem",
              }}
            >
              <AbcFitnessBrandLogo to="" theme="light" />
              <div
                style={{
                  fontWeight: 800,
                  fontSize: 16,
                  color: "var(--t1)",
                  marginTop: ".75rem",
                }}
              >
                ABC Fitness Developer Portal
              </div>
            </div>

            <div className="auth-card" data-testid="home-signup-success-card">
              <div
                className="auth-hdr"
                style={{ background: "var(--slate-dark)" }}
              >
                <p className="auth-eye">Check your email</p>
                <h1 data-testid="home-signup-success-title">
                  Verify your email to continue
                </h1>
                <p>
                  We sent a verification link to{" "}
                  <strong data-testid="home-signup-success-email">
                    {registeredEmail}
                  </strong>
                  . Open the link to activate your account, then log in.
                </p>
              </div>
              <div className="auth-body">
                <div
                  className="success-msg"
                  data-testid="home-signup-success"
                  role="status"
                >
                  <i className="ti ti-mail-check" />
                  <span>
                    Account created. Check your inbox for the verification
                    email.
                  </span>
                </div>

                {resendNotice ? (
                  <div
                    className="success-msg"
                    data-testid="home-signup-resend-success"
                    role="status"
                    style={{ marginTop: ".75rem" }}
                  >
                    <i className="ti ti-circle-check" />
                    <span>{resendNotice}</span>
                  </div>
                ) : null}

                {resendError ? (
                  <div
                    className="error-msg"
                    data-testid="home-signup-resend-error"
                    role="alert"
                    style={{ marginTop: ".75rem" }}
                  >
                    <i className="ti ti-alert-circle" />
                    <span>{resendError}</span>
                  </div>
                ) : null}

                <form
                  className="auth-form"
                  data-testid="home-signup-resend-form"
                  onSubmit={(event) => {
                    void handleResend(event);
                  }}
                >
                <button
                  type="submit"
                  className="submit-btn"
                  data-testid="home-signup-resend"
                  disabled={resendLoading || resendCooldown.isCoolingDown}
                  style={{
                    width: "100%",
                    justifyContent: "center",
                    marginTop: "1rem",
                    opacity:
                      resendLoading || resendCooldown.isCoolingDown ? 0.7 : 1,
                  }}
                >
                  {resendLoading ? (
                    <>
                      <i
                        className="ti ti-loader-2"
                        style={{ animation: "spin 1s linear infinite" }}
                      />{" "}
                      Sending…
                    </>
                  ) : (
                    <>
                      <i className="ti ti-mail" />{" "}
                      {formatVerificationResendCooldownLabel(
                        resendCooldown.secondsRemaining,
                      )}
                    </>
                  )}
                </button>
                </form>

                <Link
                  to="/login"
                  state={loginLinkState}
                  className="submit-btn"
                  data-testid="home-signup-success-login"
                  style={{
                    width: "100%",
                    justifyContent: "center",
                    marginTop: ".75rem",
                    textDecoration: "none",
                    background: "transparent",
                    border: "1px solid var(--border)",
                    color: "var(--t1)",
                  }}
                >
                  Go to log in
                </Link>
              </div>
            </div>
          </div>
        </main>
      </>
    );
  }

  return (
    <>
      <HomeTopNav />
      <main id="main-content" className="auth-shell">
        <div style={{ width: "100%", maxWidth: 560 }}>
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              marginBottom: "1.5rem",
            }}
          >
            <AbcFitnessBrandLogo to="" theme="light" />
            <div style={{ fontWeight: 800, fontSize: 16, color: "var(--t1)", marginTop: ".75rem" }}>
              ABC Fitness Developer Portal
            </div>
          </div>

          <div className="auth-card" data-testid="home-signup-card">
            <div
              className="auth-hdr"
              style={{ background: "var(--slate-dark)" }}
            >
              <p className="auth-eye">Get started</p>
              <h1 data-testid="home-signup-title">Create your account</h1>
              <p>
                Register for developer access to the Glofox platform. We&apos;ll
                email you a link to verify your address before you can log in.
              </p>
            </div>
            <div className="auth-body">
              {requestAccessNotice ? (
                <div
                  className="notice-msg"
                  data-testid="home-signup-request-access-notice"
                  role="status"
                  style={{ marginBottom: "1rem" }}
                >
                  <i className="ti ti-info-circle" />
                  <span>{requestAccessNotice}</span>
                </div>
              ) : null}

              {error ? (
                <SignupErrorBanner error={error} loginLinkState={loginLinkState} />
              ) : null}

              <form
                className="auth-form"
                data-testid="home-signup-form"
                noValidate
                onSubmit={(event) => {
                  void handleSubmit(event);
                }}
              >
              <div className="field-row">
                <div className="field">
                  <label htmlFor="home-signup-company-name">
                    Business / Studio <span className="req">*</span>
                  </label>
                  <input
                    id="home-signup-company-name"
                    type="text"
                    data-testid="home-signup-company-name"
                    placeholder="e.g. FitLife Studios Ltd."
                    value={companyName}
                    onChange={(e) => {
                      setCompanyName(e.target.value);
                      clearError();
                    }}
                  />
                </div>

                <div className="field">
                  <label htmlFor="home-signup-contact-name">
                    Contact name <span className="req">*</span>
                  </label>
                  <input
                    id="home-signup-contact-name"
                    type="text"
                    data-testid="home-signup-contact-name"
                    placeholder="e.g. Jordan Smith"
                    value={contactName}
                    onChange={(e) => {
                      setContactName(e.target.value);
                      clearError();
                    }}
                  />
                </div>
              </div>

              <div className="field">
                <label htmlFor="home-signup-email">
                  Business email <span className="req">*</span>
                </label>
                <input
                  id="home-signup-email"
                  type="email"
                  data-testid="home-signup-email"
                  autoComplete="email"
                  placeholder="you@company.com"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    clearError();
                  }}
                />
              </div>

              <div className="field">
                <label htmlFor="home-signup-password">
                  Password <span className="req">*</span>
                </label>
                <input
                  id="home-signup-password"
                  type="password"
                  data-testid="home-signup-password"
                  autoComplete="new-password"
                  placeholder="Min. 8 characters"
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value);
                    clearError();
                  }}
                />
                <div className="pw-bar">
                  <div
                    className="pw-fill"
                    style={{
                      width: pwStrength.width,
                      background: pwStrength.color,
                    }}
                  />
                </div>
                <div
                  className="pw-label"
                  style={{
                    color: password.length ? pwStrength.color : "var(--t3)",
                  }}
                >
                  {pwStrength.label}
                </div>
              </div>

              <button
                type="submit"
                className="submit-btn"
                data-testid="home-signup-submit"
                disabled={!isValid || loading}
                style={{
                  width: "100%",
                  justifyContent: "center",
                  marginTop: ".25rem",
                  opacity: isValid ? 1 : 0.5,
                }}
              >
                {loading ? (
                  <>
                    <i
                      className="ti ti-loader-2"
                      style={{ animation: "spin 1s linear infinite" }}
                    />{" "}
                    Creating account…
                  </>
                ) : (
                  <>
                    <i className="ti ti-user-plus" /> Create account
                  </>
                )}
              </button>
              </form>
            </div>
          </div>

          <p className="already">
            Already have an account?{" "}
            <Link
              to="/login"
              state={loginLinkState}
              data-testid="home-signup-login-link"
            >
              Log in →
            </Link>
          </p>
        </div>
      </main>
    </>
  );
}
